import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

const DAYS_FR = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const MONTHS_SHORT = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc',
];

function parseDate(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length < 3) return null;
  return { year: Number(parts[0]), month: Number(parts[1]) - 1, day: Number(parts[2]) };
}

function formatDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatDisplay(str) {
  if (!str) return '';
  const d = parseDate(str);
  if (!d) return str;
  return `${String(d.day).padStart(2, '0')}/${String(d.month + 1).padStart(2, '0')}/${d.year}`;
}

function applyDateMask(value) {
  const digits = value.replace(/\D/g, '').substring(0, 8);
  let result = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += '/';
    result += digits[i];
  }
  return result;
}

function parseDisplayDate(display) {
  if (!display) return null;
  const parts = display.split('/');
  if (parts.length < 3) return null;
  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (year < 1900 || year > 2100) return null;
  if (month < 1 || month > 12) return null;
  const maxDay = getDaysInMonth(year, month - 1);
  if (day < 1 || day > maxDay) return null;
  return { year, month: month - 1, day };
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

export function DatePicker({ id, name, value, disabled, ariaInvalid, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days'); // 'days' | 'months' | 'years'
  const [textValue, setTextValue] = useState(() => formatDisplay(value));
  const [textError, setTextError] = useState(false);
  const today = useMemo(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
  }, []);

  const parsed = parseDate(value);
  const [viewYear, setViewYear] = useState(parsed?.year ?? today.year);
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.month);
  const containerRef = useRef(null);

  useEffect(() => {
    const p = parseDate(value);
    if (p) {
      setViewYear(p.year);
      setViewMonth(p.month);
    }
    // Sync text input when value changes externally (e.g. from calendar)
    setTextValue(formatDisplay(value));
    setTextError(false);
  }, [value]);

  const handleTextInput = useCallback((e) => {
    const masked = applyDateMask(e.target.value);
    setTextValue(masked);

    if (masked.length === 10) {
      const p = parseDisplayDate(masked);
      if (p) {
        onChange(formatDate(p.year, p.month, p.day));
        setTextError(false);
      } else {
        setTextError(true);
      }
    } else {
      setTextError(false);
      if (masked.length === 0) {
        onChange('');
      }
    }
  }, [onChange]);

  const handleTextBlur = useCallback(() => {
    if (textValue.length > 0 && textValue.length < 10) {
      setTextValue(formatDisplay(value));
    }
    setTextError(false);
  }, [textValue, value]);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  }

  function selectDay(day) {
    onChange(formatDate(viewYear, viewMonth, day));
    setOpen(false);
  }

  function clearValue(e) {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  }

  function handleOpen() {
    setViewMode('days');
    const p = parseDate(value);
    if (p) { setViewYear(p.year); setViewMonth(p.month); }
    setOpen(true);
  }

  // Build day cells
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const prevDays = getDaysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);

  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, current: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true });
  }
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, current: false });
    }
  }

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  // Year grid for year picker
  const decadeStart = Math.floor(viewYear / 10) * 10 - 1;
  const yearCells = [];
  for (let y = decadeStart; y <= decadeStart + 11; y++) {
    yearCells.push(y);
  }

  return (
    <div className="cat-datepicker" ref={containerRef}>
      <div
        className={`cat-datepicker__input cat-react-input${disabled ? ' cat-datepicker__input--disabled' : ''}`}
        aria-invalid={ariaInvalid}
      >
        <input
          type="text"
          className={`cat-datepicker__text-input${textError ? ' cat-datepicker__text-input--error' : ''}`}
          placeholder="jj/mm/aaaa"
          inputMode="numeric"
          maxLength={10}
          value={textValue}
          disabled={disabled}
          onChange={handleTextInput}
          onBlur={handleTextBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        />
        <span className="cat-datepicker__icons">
          {value && !disabled && (
            <button type="button" className="cat-datepicker__clear" onClick={clearValue} aria-label="Effacer" tabIndex={-1}>
              &times;
            </button>
          )}
          <svg
            className="cat-datepicker__icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            width="18"
            height="18"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            onClick={() => !disabled && (open ? setOpen(false) : handleOpen())}
          >
            <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 8h12v8H4V8z" />
          </svg>
        </span>
      </div>
      <input type="hidden" id={id} name={name} value={value ?? ''} />
      {open && (
        <div className="cat-datepicker__dropdown">
          {viewMode === 'days' && (
            <>
              <div className="cat-datepicker__header">
                <button type="button" className="cat-datepicker__nav" onClick={prevMonth} aria-label="Mois précédent">&lsaquo;</button>
                <button type="button" className="cat-datepicker__title-btn" onClick={() => setViewMode('months')}>
                  {MONTHS_FR[viewMonth]} {viewYear}
                </button>
                <button type="button" className="cat-datepicker__nav" onClick={nextMonth} aria-label="Mois suivant">&rsaquo;</button>
              </div>
              <table className="cat-datepicker__grid">
                <thead>
                  <tr>
                    {DAYS_FR.map((d) => <th key={d}>{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((week, wi) => (
                    <tr key={wi}>
                      {week.map((cell, ci) => {
                        const isToday = cell.current && cell.day === today.day && viewMonth === today.month && viewYear === today.year;
                        const isSelected = cell.current && parsed && cell.day === parsed.day && viewMonth === parsed.month && viewYear === parsed.year;
                        let cls = 'cat-datepicker__day';
                        if (!cell.current) cls += ' cat-datepicker__day--other';
                        if (isToday) cls += ' cat-datepicker__day--today';
                        if (isSelected) cls += ' cat-datepicker__day--selected';
                        return (
                          <td
                            key={ci}
                            className={cls}
                            onClick={cell.current ? () => selectDay(cell.day) : undefined}
                          >
                            {cell.day}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cat-datepicker__footer">
                <button type="button" className="cat-datepicker__footer-btn" onClick={clearValue}>Effacer</button>
                <button type="button" className="cat-datepicker__footer-btn cat-datepicker__footer-btn--today" onClick={() => { onChange(formatDate(today.year, today.month, today.day)); setOpen(false); }}>Aujourd'hui</button>
              </div>
            </>
          )}
          {viewMode === 'months' && (
            <>
              <div className="cat-datepicker__header">
                <button type="button" className="cat-datepicker__nav" onClick={() => setViewYear(viewYear - 1)} aria-label="Année précédente">&lsaquo;</button>
                <button type="button" className="cat-datepicker__title-btn" onClick={() => setViewMode('years')}>
                  {viewYear}
                </button>
                <button type="button" className="cat-datepicker__nav" onClick={() => setViewYear(viewYear + 1)} aria-label="Année suivante">&rsaquo;</button>
              </div>
              <div className="cat-datepicker__picker-grid">
                {MONTHS_SHORT.map((m, i) => {
                  let cls = 'cat-datepicker__picker-cell';
                  if (i === today.month && viewYear === today.year) cls += ' cat-datepicker__picker-cell--today';
                  if (i === viewMonth) cls += ' cat-datepicker__picker-cell--selected';
                  return (
                    <button key={i} type="button" className={cls} onClick={() => { setViewMonth(i); setViewMode('days'); }}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {viewMode === 'years' && (
            <>
              <div className="cat-datepicker__header">
                <button type="button" className="cat-datepicker__nav" onClick={() => setViewYear(viewYear - 10)} aria-label="Décennie précédente">&lsaquo;</button>
                <span className="cat-datepicker__title">{decadeStart + 1} - {decadeStart + 10}</span>
                <button type="button" className="cat-datepicker__nav" onClick={() => setViewYear(viewYear + 10)} aria-label="Décennie suivante">&rsaquo;</button>
              </div>
              <div className="cat-datepicker__picker-grid">
                {yearCells.map((y) => {
                  let cls = 'cat-datepicker__picker-cell';
                  if (y === decadeStart || y === decadeStart + 11) cls += ' cat-datepicker__picker-cell--other';
                  if (y === today.year) cls += ' cat-datepicker__picker-cell--today';
                  if (y === viewYear) cls += ' cat-datepicker__picker-cell--selected';
                  return (
                    <button key={y} type="button" className={cls} onClick={() => { setViewYear(y); setViewMode('months'); }}>
                      {y}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
